import { Capacitor } from "@capacitor/core";

/** Web only: cache the app shell so a cloud-served page opens without
 *  internet and talks to the venue hub over the LAN. Native apps (APK/exe)
 *  already ship their shell; a plain-http hub-served page can't register a
 *  service worker (browsers require https/localhost) and silently skips. */
let updateSW: ((reload?: boolean) => Promise<void>) | null = null;

/** Ask the browser never to evict this site's storage (sign-in, settings,
 *  hub identity). Best effort; the answer is reported in Settings. */
export async function requestPersistentStorage(): Promise<boolean | null> {
  try {
    if (typeof navigator === "undefined" || !navigator.storage?.persist) return null;
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return null;
  }
}

export async function storageDiagnostics(): Promise<{ persisted: boolean | null; usageKB: number | null; quotaMB: number | null; origin: string; hasSettings: boolean; hasHub: boolean; hasLaunchUrl: boolean; signedInAt: number | null }> {
  let persisted: boolean | null = null;
  let usageKB: number | null = null;
  let quotaMB: number | null = null;
  try {
    if (navigator.storage?.persisted) persisted = await navigator.storage.persisted();
    if (navigator.storage?.estimate) {
      const e = await navigator.storage.estimate();
      usageKB = e.usage != null ? Math.round(e.usage / 1024) : null;
      quotaMB = e.quota != null ? Math.round(e.quota / 1048576) : null;
    }
  } catch { /* ignore */ }
  const { kvGet } = await import("./data/idb");
  const hasSettings = !!(await kvGet("settings").catch(() => null));
  const hasHub = !!(await kvGet("hub-conn").catch(() => null));
  let hasLaunchUrl = false;
  let signedInAt: number | null = null;
  try { hasLaunchUrl = !!localStorage.getItem("dp-launch-url"); signedInAt = Number(localStorage.getItem("dp-signed-in-at")) || null; } catch { /* storage blocked */ }
  return { persisted, usageKB, quotaMB, origin: window.location.origin, hasSettings, hasHub, hasLaunchUrl, signedInAt };
}

/** The offline app shell belongs to the cloud-served page only. On the
 *  desktop server (localhost) or a dev host it is worse than useless: its
 *  fallback for any unrecognised address is the KITCHEN page, so the POS's
 *  customer window (/?customer=1) came up as a KDS. Never register there —
 *  and remove one a previous run left behind. */
const CLOUD_HOST = /(^|\.)dineplatform\.com$/i;
export function registerWebServiceWorker(): void {
  if (Capacitor.isNativePlatform()) return;
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
  if (!window.isSecureContext) return;
  if (!CLOUD_HOST.test(window.location.hostname)) {
    void navigator.serviceWorker.getRegistrations()
      .then((regs) => Promise.all(regs.map((r) => r.unregister())))
      .catch(() => {});
    return;
  }
  void import("virtual:pwa-register")
    .then(({ registerSW }) => { updateSW = registerSW({ immediate: true }); })
    .catch(() => {});
}

/** "Refresh site": fetch the newest build if there is one, then reload the
 *  page the way it was launched (same app/store/hub/kds params). Works on
 *  every host: the service worker path on the cloud page, a plain reload
 *  on the hub-served page and in the native shells. */
export async function refreshSite(): Promise<void> {
  const { launchUrl } = await import("./config/runtime");
  const target = launchUrl() || window.location.href;
  try {
    if ("serviceWorker" in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      await reg?.update();
      // update() resolves when the CHECK finishes, not when the new worker has
      // taken over. Reloading straight away leaves the old worker in control
      // and it serves the cached build again — which is why "Refresh site"
      // looked like it did nothing. Give the takeover a moment.
      if (reg?.waiting) {
        reg.waiting.postMessage({ type: "SKIP_WAITING" });
        await new Promise<void>((resolve) => {
          const done = () => resolve();
          navigator.serviceWorker.addEventListener("controllerchange", done, { once: true });
          setTimeout(done, 3000);
        });
      }
    }
    if (updateSW) { await updateSW(false); }
    // Still on the old build? Then stop being polite about it. A refresh the
    // user explicitly asked for must not hand back the same build; a kiosk sat
    // on a stale one for hours because this step did not exist.
    const mine = document.querySelector<HTMLScriptElement>('script[type="module"][src]')?.getAttribute("src") || "";
    if (entryFamily(mine)) {
      const html = await fetch(window.location.pathname, { cache: "reload" }).then((r) => r.text()).catch(() => "");
      if (buildDrifted(mine, html)) {
        if (typeof caches !== "undefined") {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }
        const reg = await navigator.serviceWorker?.getRegistration();
        await reg?.unregister().catch(() => {});
      }
    }
  } catch { /* offline or no SW — just reload */ }
  const u = new URL(target, window.location.href);
  u.searchParams.set("_r", String(Date.now()));
  window.location.replace(u.toString());
}

/** Which deployed build a page is running, and whether it has drifted.
 *  Compared WITHIN one entry family only (a POS page against index.html's
 *  `main-*`, a kitchen page against kds.html's `kds-*`): the first version of
 *  this watch asked every page for kds.html, so the POS compared its own
 *  `main-*` against the KDS's `kds-*`, never matched, and wiped its caches
 *  and reloaded every five minutes — a white screen mid-service. */
export function entryFamily(src: string): "main" | "kds" | null {
  const m = /assets\/(kds|main)-[A-Za-z0-9_-]+\.js/.exec(src || "");
  return m ? (m[1] as "main" | "kds") : null;
}
export function buildDrifted(mineSrc: string, servedHtml: string): boolean {
  const family = entryFamily(mineSrc);
  if (!family) return false;
  const live = new RegExp(`assets/${family}-[A-Za-z0-9_-]+\\.js`).exec(servedHtml || "")?.[0];
  // No entry of our family in the answer (an error page, or the other
  // document): nothing we can conclude — never act on it.
  if (!live) return false;
  return !mineSrc.endsWith(live);
}

/** Build watch: kiosks must converge on the deployed build by themselves.
 *  Every few minutes compare the entry chunk this page runs with the one the
 *  server hands out now for THIS page. When they differ, give the service
 *  worker one chance to update; if the page is still old after that, drop the
 *  offline cache and reload. Seen on an Android 4.4 kiosk (Gecko 68):
 *  restarts and "Refresh site" kept serving the cached build for hours.
 *  Web only; the native shells ship their own build. */
let buildWatchStarted = false;
export function startBuildWatch(): void {
  if (buildWatchStarted || Capacitor.isNativePlatform() || typeof document === "undefined") return;
  buildWatchStarted = true;
  const mine = document.querySelector<HTMLScriptElement>('script[type="module"][src]')?.getAttribute("src") || "";
  if (!entryFamily(mine)) return;
  // The page's own URL: every host maps its SPA routes back to the document
  // this page was served from (index.html for the POS, kds.html for a
  // kitchen screen), so this always asks about the right one.
  const doc = location.pathname || "/";
  // A reload must never become a loop — but the old guard (two heals per
  // browser session, ever) meant a screen that had updated twice then froze
  // on an old build for the rest of the night. Seen live: two kitchen screens
  // stuck two hours behind while deploys kept coming.
  //
  // The rate limit stays (never twice within ten minutes). The lifetime cap
  // is replaced by a test of whether healing WORKS: each attempt records the
  // entry it started from, and a heal that leaves the page on that same entry
  // achieved nothing. Two of those in an hour is a genuine loop, so stop.
  // A heal that does move the build leaves a different entry behind, so the
  // count never accumulates and screens keep up with as many deploys as
  // there are.
  const HEALS = "dp-build-heals";
  type Heal = { at: number; from: string };
  const heals = (): Heal[] => {
    try {
      const raw = JSON.parse(sessionStorage.getItem(HEALS) || "[]");
      return (Array.isArray(raw) ? raw : [])
        .map((h: any) => (typeof h === "number" ? { at: h, from: "" } : h))
        .filter((h: Heal) => h && Date.now() - h.at < 60 * 60_000);
    } catch {
      return [];
    }
  };
  let healing = false;
  const check = async () => {
    if (healing || !navigator.onLine) return;
    const past = heals();
    if (past.length && Date.now() - past[past.length - 1].at < 10 * 60_000) return;
    if (past.filter((h) => h.from === mine).length >= 2) return; // healing this entry does nothing
    try {
      // `cache: "no-store"` only bypasses the HTTP cache — the request still
      // goes through the service worker, whose precache answered with the OLD
      // kds.html, so the probe never saw a drift and three kitchen screens
      // sat on a stale build through four deploys (2026-09-21). A query the
      // precache does not match sends the probe to the network.
      const html = await (await fetch(`${doc}${doc.includes("?") ? "&" : "?"}probe=${Date.now()}`, { cache: "no-store" })).text();
      if (!buildDrifted(mine, html)) return;
      healing = true;
      try { sessionStorage.setItem(HEALS, JSON.stringify([...past, { at: Date.now(), from: mine }])); } catch { /* private mode */ }
      console.info("[build] a newer build is deployed — updating this screen");
      try { await (await navigator.serviceWorker?.getRegistration())?.update(); } catch { /* no worker */ }
      await new Promise((r) => setTimeout(r, 12_000));
      // Still here: the worker didn't take the update, so clear and reload.
      try {
        const regs = (await navigator.serviceWorker?.getRegistrations()) || [];
        await Promise.all(regs.map((r) => r.unregister()));
      } catch { /* no worker */ }
      try {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      } catch { /* no cache API */ }
      location.reload();
    } catch {
      healing = false; // offline or blocked: try again next tick
    }
  };
  window.setTimeout(() => void check(), 60_000);
  window.setInterval(() => void check(), 15 * 60_000);
}
